import { useState } from 'react';
import { BarChart, DonutChart, LineChart, Sparkline } from '../components/charts.tsx';
import { Button, Card, EmptyState, Field, KpiCard, Menu, PageHeader, Skeleton, StatusBadge, Switch, Tabs } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { useUi } from '../lib/ui-state.tsx';
import { Shell } from '../components/Shell.tsx';

/**
 * The design system gallery: every shared component in the current theme, for review. The
 * figures are examples and say so; no business data is read here.
 */

const COLOURS = [
  'bg',
  'surface',
  'surface-2',
  'border',
  'text',
  'text-2',
  'text-3',
  'primary',
  'primary-soft',
  'ok-fill',
  'warn-fill',
  'danger-fill',
  'nav',
  'nav-2',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'chart-6',
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const A = [42, 48, 45, 53, 61, 58, 66, 72, 69, 77, 81, 88];
const B = [30, 33, 35, 34, 40, 43, 41, 47, 50, 49, 55, 57];

export function Design() {
  const { theme, setTheme } = useUi();
  const [tab, setTab] = useState<'one' | 'two' | 'three'>('one');
  const [on, setOn] = useState(true);
  return (
    <Shell>
      <div className="page ds-page">
        <PageHeader
          eyebrow={t('dsg.eyebrow')}
          title={t('dsg.title')}
          subtitle={t('dsg.subtitle')}
          actions={
            <>
              <Button icon="sun" variant={theme === 'light' ? 'primary' : 'secondary'} onClick={() => setTheme('light')}>
                {t('dsg.light')}
              </Button>
              <Button icon="moon" variant={theme === 'dark' ? 'primary' : 'secondary'} onClick={() => setTheme('dark')}>
                {t('dsg.dark')}
              </Button>
            </>
          }
        />

        <Card title={t('dsg.colours')} subtitle={t('dsg.colours_hint')}>
          <div className="ds-swatches">
            {COLOURS.map((c) => (
              <div key={c} className="ds-swatch">
                <span style={{ background: `var(--${c})` }} />
                <code>{c}</code>
              </div>
            ))}
          </div>
        </Card>

        <Card title={t('dsg.type')}>
          <div className="ds-type">
            <p className="ds-t-display">{t('dsg.sample_title')}</p>
            <h1>{t('dsg.sample_page')}</h1>
            <h2>{t('dsg.sample_section')}</h2>
            <p className="card-title">{t('dsg.sample_card')}</p>
            <p>{t('dsg.sample_body')}</p>
            <p className="muted small">{t('dsg.sample_caption')}</p>
            <p className="ds-t-figure">1,284,560.00 L</p>
          </div>
        </Card>

        <div className="ds-kpis">
          <KpiCard label={t('dsg.k_revenue')} value="4,820,300 L" icon="trendUp" change={12.4} compare={t('dsg.k_vs')} trend={<Sparkline values={A} label={t('dsg.example')} />} />
          <KpiCard
            label={t('dsg.k_expenses')}
            value="2,110,900 L"
            icon="cash"
            change={6.1}
            good="down"
            compare={t('dsg.k_vs')}
            trend={<Sparkline values={B} color="chart-4" label={t('dsg.example')} />}
          />
          <KpiCard label={t('dsg.k_open')} value="934,383 L" icon="invoice" change={-3.2} good="down" compare={t('dsg.k_vs')} />
          <KpiCard label={t('dsg.k_loading')} value="" loading />
        </div>

        <div className="ds-grid">
          <Card title={t('dsg.line')} subtitle={t('dsg.example')}>
            <LineChart
              area
              labels={MONTHS}
              series={[
                { label: t('dsg.k_revenue'), values: A },
                { label: t('dsg.k_expenses'), values: B },
              ]}
              label={t('dsg.example')}
            />
          </Card>
          <Card title={t('dsg.bars')} subtitle={t('dsg.example')}>
            <BarChart
              labels={MONTHS}
              series={[
                { label: t('dsg.this_year'), values: A },
                { label: t('dsg.last_year'), values: B, color: 'chart-6' },
              ]}
              label={t('dsg.example')}
            />
          </Card>
          <Card title={t('dsg.donut')} subtitle={t('dsg.example')}>
            <DonutChart
              label={t('dsg.example')}
              total="1,240"
              centre={t('dsg.items')}
              parts={[
                { label: t('dsg.p_in_stock'), value: 820, color: 'chart-3' },
                { label: t('dsg.p_low'), value: 310, color: 'chart-4' },
                { label: t('dsg.p_out'), value: 110, color: 'danger-fill' },
              ]}
            />
          </Card>
          <Card title={t('dsg.controls')}>
            <div className="ds-row">
              <Button variant="primary" icon="plus">
                {t('dsg.primary')}
              </Button>
              <Button>{t('dsg.secondary')}</Button>
              <Button variant="ghost">{t('dsg.ghost')}</Button>
              <Button variant="danger" icon="trash">
                {t('dsg.danger')}
              </Button>
              <Menu
                label={t('dsg.more_actions')}
                items={[
                  { label: t('dsg.primary'), icon: 'edit', onSelect: () => undefined },
                  { label: t('dsg.secondary'), icon: 'copy', onSelect: () => undefined },
                  'divider',
                  { label: t('dsg.danger'), icon: 'trash', danger: true, onSelect: () => undefined },
                ]}
              />
            </div>
            <div className="ds-row">
              <StatusBadge tone="ok" icon="checkCircle" label={t('dsg.s_paid')} />
              <StatusBadge tone="warn" icon="clock" label={t('dsg.s_due')} />
              <StatusBadge tone="danger" icon="alert" label={t('dsg.s_overdue')} />
              <StatusBadge tone="info" icon="send" label={t('dsg.s_sent')} />
              <StatusBadge tone="neutral" icon="edit" label={t('dsg.s_draft')} />
            </div>
            <Tabs
              label={t('dsg.tabs')}
              value={tab}
              onChange={setTab}
              tabs={[
                { key: 'one', label: t('dsg.tab_overview'), icon: 'chart' },
                { key: 'two', label: t('dsg.tab_details'), icon: 'list', count: 12 },
                { key: 'three', label: t('dsg.tab_history'), icon: 'history' },
              ]}
            />
            <div className="ds-form">
              <Field label={t('dsg.f_name')} required hint={t('dsg.f_hint')}>
                <input className="input" placeholder={t('dsg.f_placeholder')} />
              </Field>
              <Field label={t('dsg.f_amount')} error={t('dsg.f_error')}>
                <input className="input" defaultValue="-12" />
              </Field>
              <label className="check-row">
                <Switch checked={on} onChange={setOn} label={t('dsg.f_switch')} />
                <span>{t('dsg.f_switch')}</span>
              </label>
            </div>
          </Card>
          <Card title={t('dsg.states')}>
            <Skeleton h={14} w="70%" />
            <Skeleton h={14} w="90%" />
            <Skeleton h={14} w="50%" />
            <EmptyState icon="inbox" title={t('dsg.empty_title')} body={t('dsg.empty_body')} action={<Button icon="plus">{t('dsg.primary')}</Button>} />
          </Card>
        </div>
      </div>
    </Shell>
  );
}
